import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ErrorMessage from './Components/ErrorMessage';
import {
  ConfirmationRoot,
  Header,
  IconBack,
  SectionCards,
  Button,
  SelectContainer,
} from './styles';
import { navigationUrl } from '../../store/analytic/actions';
import { page } from '../../store/auth/actions';
import BreadCrumb from '../../components/BreadCrumb';
import { HandlerNextAction } from '../AbstractNegotiation/AbstractNegotiationService';
import ConfirmationAddress from '../../components/Confirmation/ConfirmationAddress';
import IncorrectContact from '../../components/IncorrectContact';
import StepCounter from '../../components/StepCouter';
import api from '../../services/api';
import ConfirmData from '../../components/Modal/ConfirmData';

type NegotiationProps = {
  contract: GQL.IDebt;
  negotiationOption: GQL.IOption;
  addresses: GQL.IAddressConfirmation[];
  questions: GQL.IQuestionConfirmation[];
};

type Steps = {
  step: number;
  name: string;
  title: string;
};

const steps: Steps[] = [
  { step: 0, name: '', title: '' },
  { step: 1, name: 'Address', title: 'Confirme seu endereço abaixo: ' },
  { step: 2, name: 'AutoDebt', title: 'Débito automático: ' },
  { step: 3, name: 'EspecialCheck', title: 'Cheque especial: ' },
];

const SelectAddress: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation<NegotiationProps>();
  const history = useHistory();
  const { negotiationOption, contract, addresses, questions } = location.state;
  const [loading, setLoading] = useState(false);
  const [automaticDebt, setAutomaticDebt] = useState<number | null>(null);
  const [specialCheck, setSpecialCheck] = useState<number | null>(null);
  const [addressCode, setAddressCode] = useState<string | number | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [apiResponse, setApiResponse] = useState(Boolean);
  const [showAddressModal, setShowAddressModal] = useState(false);

  useEffect(() => {
    const fetchApiResponse = async () => {
      try {
        const response = await api.getIfRegisterIsAllowed({
          credorDatabaseId: contract.databaseId,
          creditorId: contract.creditorId,
        });

        setApiResponse(response?.confirmaDadosCadastrais === true);
      } catch (error) {
        console.error('Erro ao buscar dados da API:', error);
      }
    };

    fetchApiResponse();
  }, [contract.creditorId, contract.databaseId]);

  const mapperAddresses = useCallback(() => {
    return addresses.map((address) => ({
      ...address,
      complemento: address.complemento || '',
    }));
  }, [addresses]);

  useEffect(() => {
    dispatch(page('confirmarEndereço'));
    dispatch(
      navigationUrl.request({
        action: '/confirmarEndereço',
      })
    );
  }, [dispatch]);

  const finishDeal = useCallback(async () => {
    setLoading(true);
    try {
      await HandlerNextAction({
        negotiationOption: negotiationOption,
        contract: contract,
        databaseId: contract.databaseId,
        history,
        dealInfos: {
          contrato: contract.contracts,
          opcaoID: `${negotiationOption.optionId}`,
          valorEntrada: negotiationOption.firstPortionValue || 0,
          valorDemais: negotiationOption.othersPortionsValue || 0,
          auxiliar: String(addressCode),
          autorizaDebitoAutomatico: automaticDebt,
          autorizaChequeEspecial: specialCheck,
        },
      });
    } catch (error) {
      throw new Error(`Erro ao confirmar negociação:  ${error}`);
    }
  }, [
    negotiationOption,
    contract,
    addressCode,
    automaticDebt,
    specialCheck,
    history,
  ]);

  const handlerNavigation = (action: string) => {
    if (action === 'next') {
      if (currentStep + 1 >= steps.length) return finishDeal();
      setCurrentStep((state) => state + 1);
    } else {
      if (steps[currentStep - 1].step === 0) return history.goBack();
      setCurrentStep((state) => state - 1);
    }
  };

  return (
    <ConfirmationRoot>
      {addresses?.length === 0 && <ErrorMessage />}
      {addresses?.length > 0 && (
        <div className='content'>
          <Header>
            <div className='sub-section-header'>
              <div className='title-section-header'>
                <IconBack onClick={() => history.goBack()} />
                <h2 className='title'>Confirmação</h2>
              </div>
              <BreadCrumb
                currentPage='Confirmação'
                previouPages={[
                  { name: 'Débitos e Acordos', route: '/contrato' },
                  { name: 'Meus Débito', route: '/negociacao' },
                  { name: 'Resumo', route: '/resumo-negociacao' },
                ]}
                showBreadCrumbOnMinWidth='960px'
              />
              {!!questions?.length && (
                <div className='step-counter-mobile'>
                  <StepCounter
                    stepsQty={steps.length - 1}
                    current={currentStep}
                  />
                </div>
              )}
            </div>

            {!!questions?.length ? (
              <div className='sub-section-header'>
                <h3 className='step-title'>{steps[currentStep].title}</h3>
                <div className='step-counter'>
                  <StepCounter
                    stepsQty={steps.length - 1}
                    current={currentStep}
                  />
                </div>
              </div>
            ) : (
              <div className='sub-section-header'>
                <h3 className='step-title'>Confirme seu endereço abaixo: </h3>
              </div>
            )}
          </Header>

          <SectionCards
            hiddeStep={
              questions?.length ? !(steps[currentStep].step === 1) : false
            }
          >
            <ConfirmationAddress
              addresses={mapperAddresses()}
              selectedId={addressCode!}
              setSelectedId={setAddressCode!}
              preferentialIdToSelect='idExterno'
              allowNewAddress={apiResponse}
              onClickAddNewAddress={() => setShowAddressModal(true)}
            />
          </SectionCards>

          {questions?.length && (
            <div className='questions'>
              <SectionCards
                hiddeStep={
                  questions?.length ? !(steps[currentStep].step === 2) : false
                }
              >
                {questions[0] && (
                  <>
                    <span className='question'>{questions[0].questao}</span>
                    <div className='question-options'>
                      <div
                        className='answer-yes'
                        onClick={() => setAutomaticDebt(1)}
                      >
                        <SelectContainer selected={automaticDebt === 1}>
                          <span />
                        </SelectContainer>
                        <span>Sim</span>
                      </div>

                      <div
                        className='answer-no'
                        onClick={() => setAutomaticDebt(2)}
                      >
                        <SelectContainer selected={automaticDebt === 2}>
                          <span />
                        </SelectContainer>
                        <span>Não</span>
                      </div>
                    </div>
                  </>
                )}
              </SectionCards>

              <SectionCards
                hiddeStep={
                  questions?.length ? !(steps[currentStep].step === 3) : false
                }
              >
                {questions[1] && (
                  <>
                    <span className='question'>{questions[1].questao}</span>
                    <div className='question-options'>
                      <div
                        className='answer-yes'
                        onClick={() => setSpecialCheck(1)}
                      >
                        <SelectContainer selected={specialCheck === 1}>
                          <span />
                        </SelectContainer>
                        <span>Sim</span>
                      </div>
                      <div
                        className='answer-no'
                        onClick={() => setSpecialCheck(2)}
                      >
                        <SelectContainer selected={specialCheck === 2}>
                          <span />
                        </SelectContainer>
                        <span>Não</span>
                      </div>
                    </div>
                  </>
                )}
              </SectionCards>
            </div>
          )}
          <div className='footer'>
            <Button
              color='white'
              onClick={
                questions?.length
                  ? () => handlerNavigation('previous')
                  : () => history.goBack()
              }
            >
              Voltar
            </Button>
            <Button
              color='primary'
              onClick={
                questions?.length
                  ? () => handlerNavigation('next')
                  : () => finishDeal()
              }
              loading={loading}
              disabled={
                (questions?.length
                  ? currentStep === 1
                    ? !addressCode
                    : currentStep === 2
                    ? !automaticDebt
                    : !specialCheck
                  : !addressCode) || loading
              }
            >
              Próximo
            </Button>
          </div>
          <IncorrectContact />
        </div>
      )}

      {showAddressModal && (
        <ConfirmData
          code='Endereco'
          handlerClose={() => setShowAddressModal(false)}
          sendData={() => setShowAddressModal(false)}
          setDataModal={setShowAddressModal}
        />
      )}
    </ConfirmationRoot>
  );
};

export default SelectAddress;
